import { averageScore, scoreStatistics, isScored } from '@/lib/scoreStatistics';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { setAgentArchived } from '@/lib/api/agentLifecycle';

// ============================================
// TENANT ISOLATION HELPERS
// ============================================

// Master tenant ID (MEDD-SIM) - this tenant sees ALL platform data
const MASTER_TENANT_ID = '00000000-0000-0000-0000-000000000001';

// Helper to check if a tenant is the master tenant
export function isMasterTenant(tenantId?: string): boolean {
  return !tenantId || tenantId === MASTER_TENANT_ID;
}

// Tables that have tenant_id column
const TABLES_WITH_TENANT_ID = ['profiles', 'simulation_agents', 'companies', 'simulation_evaluations', 'learning_pathways', 'coaching_templates'];

// Helper to check if table has tenant_id
function tableHasTenantId(table: string): boolean {
  return TABLES_WITH_TENANT_ID.includes(table);
}

// ============================================
// COMPANY CREATION API
// ============================================

export async function createCompany(companyData: {
  name: string;
  industry?: string;
  logo_url?: string;
  banner_image_url?: string;
  website?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  is_active?: boolean;
  tenant_id?: string; // Required for tenant admins creating branches
}): Promise<{ success: boolean; error?: string; companyId?: string }> {
  try {
    const { data, error } = await supabase
      .from('companies')
      .insert({
        name: companyData.name,
        industry: companyData.industry || null,
        logo_url: companyData.logo_url || null,
        banner_image_url: companyData.banner_image_url || null,
        website: companyData.website || null,
        contact_email: companyData.contact_email || null,
        contact_phone: companyData.contact_phone || null,
        address: companyData.address || null,
        city: companyData.city || null,
        state: companyData.state || null,
        country: companyData.country || 'Australia',
        is_active: companyData.is_active ?? true,
        tenant_id: companyData.tenant_id || null, // Assign to tenant
      })
      .select()
      .single();

    if (error) {
      if (error.code === '42501') {
        throw new Error('Permission denied. Only administrators can create companies.');
      }
      throw error;
    }

    toast.success('Company created successfully');
    return { success: true, companyId: data.id };
  } catch (error: any) {
    console.error('❌ Create company failed:', error);
    toast.error(error.message || 'Failed to create company');
    return { success: false, error: error.message };
  }
}

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  company?: string;
  company_id?: string;
  job_title?: string;
  location?: string;
  avatar_url?: string;
  status?: string;
  created_at: string;
}

export interface Agent {
  id: string;
  type: string;
  name: string;
  title: string;
  specialty?: string;
  initials: string;
  age?: number;
  gender?: string;
  location?: string;
  status: string;
  difficulty?: string;
  goal_score: number;
  completions: number;
  avg_score?: number;
  estimated_duration?: string;
  personality?: string;
  communication_style?: string;
  backstory?: string;
  objectives?: string;
  created_at?: string;
  experience?: number;
  image_url?: string;
  user_best_score?: number;
  platform?: string;
}

export interface SimulationEvaluation {
  id: string;
  user_id: string;
  agent_id: string;
  session_id: string;
  overall_score?: number;
  detailed_evaluation?: any;
  evaluated_at?: string;
  created_at?: string;
  agent?: Agent;
}

// ============================================
// USERS API
// ============================================

export async function fetchUsers(filters?: {
  search?: string;
  role?: string;
  company?: string;
  status?: string;
}) {
  try {
    // Fetch profiles with companies (no user_roles join)
    let query = supabase
      .from('profiles')
      .select(`
        *,
        companies (id, name)
      `);

    if (filters?.search) {
      query = query.or(`display_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    if (filters?.company && filters.company !== 'all') {
      query = query.eq('company_id', filters.company);
    }

    const { data: profiles, error } = await query;
    if (error) throw error;

    // Fetch all user roles separately
    const { data: userRoles } = await supabase
      .from('user_roles')
      .select('user_id, role');

    // Create lookup map: auth_user_id -> role
    const roleMap = new Map<string, string>(
      userRoles?.map(ur => [ur.user_id, ur.role]) || []
    );

    // Transform and join data
    const users = profiles?.map(user => ({
      id: user.id,
      name: user.display_name,
      email: user.email,
      phone: user.phone || '',
      // Lookup role from map, default to 'user' if not found
      role: (user.auth_user_id && roleMap.get(user.auth_user_id)) || 'user',
      company: user.companies?.name,
      company_id: user.company_id,
      job_title: user.job_title || '',
      location: user.location || '',
      avatar_url: user.avatar_url || '',
      status: 'active',
      created_at: new Date(user.created_at).toLocaleDateString(),
    })) || [];

    // Apply role filter if specified
    if (filters?.role && filters.role !== 'all') {
      return users.filter(u => u.role === filters.role);
    }

    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    toast.error('Failed to load users');
    return [];
  }
}

export async function fetchUserStats() {
  try {
    const { count: totalUsers } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true });

    // Get role counts
    const { data: roleData } = await supabase
      .from('user_roles')
      .select('role');

    const adminCount = roleData?.filter(r => r.role === 'admin').length || 0;
    const salesRepCount = roleData?.filter(r => r.role === 'sales_rep').length || 0;
    const companyAdminCount = roleData?.filter(r => r.role === 'company_admin').length || 0;

    return {
      total: totalUsers || 0,
      admins: adminCount,
      salesReps: salesRepCount,
      companyAdmins: companyAdminCount,
    };
  } catch (error) {
    console.error('Error fetching user stats:', error);
    return { total: 0, admins: 0, salesReps: 0, companyAdmins: 0 };
  }
}

// ============================================
// AGENTS API
// ============================================

export async function fetchAgents(filters?: {
  search?: string;
  type?: string;
  specialty?: string;
  status?: string;
  limit?: number;
  company_id?: string;
  user_id?: string;
  tenant_id?: string; // For white-label data isolation
}) {
  try {
    let query = supabase
      .from('simulation_agents')
      .select('*')
      .neq('is_template', true);

    // Filter agents based on tenant context
    // MEDD (master tenant): Only see system agents + agents MEDD created
    // White-label tenants: Only see their tenant's agents + system agents
    const isSystemAgentQuery = filters?.company_id === 'global';
    
    if (filters?.tenant_id) {
      if (isMasterTenant(filters.tenant_id)) {
        // MEDD sees: system agents OR agents created by MEDD (not tenant-created)
        // This prevents MEDD from seeing agents that tenant admins created
        if (!isSystemAgentQuery) {
          query = query.or('is_system.eq.true,created_by_master.eq.true');
        }
      } else if (tableHasTenantId('simulation_agents') && !isSystemAgentQuery) {
        // White-label tenant: only see their tenant's agents
        query = query.eq('tenant_id', filters.tenant_id);
        // Exclude system agents (they're fetched separately with company_id='global')
        query = query.neq('is_system', true);
      }
    }

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,specialty.ilike.%${filters.search}%,title.ilike.%${filters.search}%`);
    }

    if (filters?.type && filters.type !== 'all') {
      // Support comma-separated types for category grouping (e.g., "patient,customer" for roleplays)
      if (filters.type.includes(',')) {
        query = query.in('type', filters.type.split(','));
      } else {
        query = query.eq('type', filters.type);
      }
    }

    if (filters?.specialty && filters.specialty !== 'all') {
      query = query.ilike('specialty', filters.specialty);
    }

    if (filters?.status === 'all') {
      // Reserved for an explicit archive-management view.
    } else {
      query = query.eq('status', filters?.status || 'active');
    }

    if (filters?.company_id) {
      if (filters.company_id === 'global') {
        // Show SYSTEM agents only:
        // 1. Explicitly marked as system template (is_system=true)
        // 2. OR master tenant agents with company_id=null (legacy global agents from MEDD)
        // NOTE: Tenant agents should NEVER appear here - they must have company_id set
        query = query.eq('is_system', true);
      } else {
        // Show only agents from this specific company
        query = query.eq('company_id', filters.company_id);
      }
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data: agents, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;

    // If user_id provided, fetch their best scores per agent
    if (filters?.user_id && agents) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return agents;

      const { data: evaluations } = await supabase
        .from('simulation_evaluations')
        .select('simulation_id, overall_score')
        .eq('auth_user_id', user.id)
        .eq('is_submitted', true)
        .not('overall_score', 'is', null)
        .order('overall_score', { ascending: false });

      // Create map of agent_id -> best_score
      const bestScores = new Map<string, number>();
      evaluations?.forEach(e => {
        const agentId = e.simulation_id;
        if (agentId && (!bestScores.has(agentId) || (e.overall_score && e.overall_score > (bestScores.get(agentId) || 0)))) {
          bestScores.set(agentId, e.overall_score || 0);
        }
      });

      // Add best scores to agents
      return agents.map(agent => ({
        ...agent,
        user_best_score: bestScores.get(agent.id) || null,
      }));
    }

    return agents || [];
  } catch (error) {
    console.error('Error fetching agents:', error);
    toast.error('Failed to load agents');
    return [];
  }
}

export async function fetchAgentById(id: string) {
  try {
    const { data, error } = await supabase
      .from('simulation_agents')
      .select(`
        *,
        agent_conditions (
          medical_conditions (*)
        ),
        agent_assessments (
          physical_assessments (*)
        )
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching agent:', error);
    toast.error('Failed to load agent details');
    return null;
  }
}

export async function fetchAgentStats(filters?: { 
  tenant_id?: string; 
  company_id?: string;
}) {
  try {
    // RLS handles filtering - just count what comes back
    // For white-label: RLS returns system agents + tenant agents (via tenant_admins check)
    // For master: RLS returns all agents (is_super_admin)
    
    const { data: allAgents } = await supabase
      .from('simulation_agents')
      .select('type, is_system, is_template, tenant_id, completions')
      .neq('is_template', true)
      .eq('status', 'active');
    
    // RLS already filtered - just count what we got
    let visibleAgents = allAgents || [];
    
    // If tenant_id filter provided, also filter client-side to match the viewed tenant
    // (user may be admin of multiple tenants but viewing one specific tenant)
    if (filters?.tenant_id && !isMasterTenant(filters.tenant_id)) {
      visibleAgents = visibleAgents.filter(a => 
        (a as any).is_system === true || (a as any).tenant_id === filters.tenant_id
      );
    }

    const totalAgents = visibleAgents.length;
    const patientAgents = visibleAgents.filter(a => a.type === 'patient').length;
    const customerAgents = visibleAgents.filter(a => a.type === 'customer').length;
    const coachAgents = visibleAgents.filter(a => a.type === 'coach').length;
    const examinerAgents = visibleAgents.filter(a => a.type === 'examiner').length;
    const activeSimulations = visibleAgents.reduce((sum, a) => sum + (a.completions || 0), 0);

    return {
      total: totalAgents,
      patient: patientAgents,
      customer: customerAgents,
      coach: coachAgents,
      examiner: examinerAgents,
      roleplays: patientAgents + customerAgents,
      activeSimulations,
    };
  } catch (error) {
    console.error('Error fetching agent stats:', error);
    return { total: 0, patient: 0, customer: 0, coach: 0, examiner: 0, roleplays: 0, activeSimulations: 0 };
  }
}

export async function archiveAgent(agentId: string, useEnterpriseLifecycle = false) {
  try {
    if (useEnterpriseLifecycle) {
      await setAgentArchived(agentId, true);
    } else {
      const { data, error } = await supabase
        .from('simulation_agents')
        .update({ status: 'inactive', updated_at: new Date().toISOString() })
        .eq('id', agentId)
        .select('id, status')
        .maybeSingle();

      if (error?.code === '42501') {
        throw new Error('Permission denied. You can only archive agents assigned to your organisation scope.');
      }
      if (error) throw error;
      if (!data || data.status !== 'inactive') {
        throw new Error('The agent was not archived. Refresh the page and try again.');
      }
    }

    toast.success('Agent archived. Existing sessions and reports have been preserved.');
    return true;
  } catch (error: any) {
    console.error('Archive agent failed:', error);
    toast.error(`Failed to archive agent: ${error.message}`);
    throw error;
  }
}

// ============================================
// MEDICAL CONDITIONS API
// ============================================

export async function fetchMedicalConditions(filters?: {
  search?: string;
  category?: string;
  severity?: string;
}) {
  try {
    let query = supabase
      .from('medical_conditions')
      .select('*');

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,icd_code.ilike.%${filters.search}%`);
    }

    if (filters?.category && filters.category !== 'all') {
      query = query.eq('category', filters.category);
    }

    if (filters?.severity && filters.severity !== 'all') {
      query = query.eq('severity', filters.severity);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;

    // Transform to match expected format
    return data?.map(condition => ({
      id: condition.id,
      name: condition.name,
      icdCode: condition.icd_code,
      category: condition.category,
      severity: condition.severity,
      description: condition.description || '',
      symptoms: condition.symptoms || [],
      physicalFindings: condition.physical_findings || '',
      diagnosticCriteria: condition.diagnostic_criteria || '',
      differentialDiagnosis: condition.differential_diagnosis || '',
      treatmentOptions: condition.treatment_options || '',
      prognosis: condition.prognosis || '',
      complications: condition.complications || '',
      patientEducation: condition.patient_education || '',
      usedBy: condition.used_by || 0,
      clinicalNotes: condition.clinical_notes || '',
    })) || [];
  } catch (error) {
    console.error('Error fetching conditions:', error);
    toast.error('Failed to load medical conditions');
    return [];
  }
}

export async function fetchPhysicalAssessmentTypes() {
  try {
    const { data, error } = await supabase
      .from('physical_assessments')
      .select('*')
      .order('name');
    
    if (error) throw error;
    
    // Return full assessment data with transformed field names
    return data?.map(assessment => ({
      id: assessment.id,
      name: assessment.name,
      system: assessment.system,
      type: assessment.type,
      description: assessment.description || '',
      generalAppearance: assessment.general_appearance || '',
      inspection: assessment.inspection || '',
      palpation: assessment.palpation || '',
      percussion: assessment.percussion || '',
      auscultation: assessment.auscultation || '',
      findings: assessment.findings || [],
      includeVitals: assessment.include_vitals || false,
      vitals: assessment.include_vitals ? {
        hr: assessment.hr || '',
        bp: assessment.bp || '',
        rr: assessment.rr || '',
        temp: assessment.temp || '',
        o2sat: assessment.o2sat || '',
      } : null,
      clinicalSignificance: assessment.clinical_significance || '',
      differentialConsiderations: assessment.differential_considerations || '',
      recommendedActions: assessment.recommended_actions || '',
      teachingPoints: assessment.teaching_points || '',
    })) || [];
  } catch (error) {
    console.error('Error fetching physical assessment types:', error);
    return [];
  }
}

// Helper function to build formatted findings text from an assessment
export function buildAssessmentFindings(assessment: any): string {
  const sections: string[] = [];
  
  if (assessment.generalAppearance) {
    sections.push(`**General Appearance:**\n${assessment.generalAppearance}`);
  }
  if (assessment.inspection) {
    sections.push(`**Inspection:**\n${assessment.inspection}`);
  }
  if (assessment.palpation) {
    sections.push(`**Palpation:**\n${assessment.palpation}`);
  }
  if (assessment.percussion) {
    sections.push(`**Percussion:**\n${assessment.percussion}`);
  }
  if (assessment.auscultation) {
    sections.push(`**Auscultation:**\n${assessment.auscultation}`);
  }
  if (assessment.findings && assessment.findings.length > 0) {
    sections.push(`**Key Findings:**\n${assessment.findings.map((f: string) => `• ${f}`).join('\n')}`);
  }
  if (assessment.includeVitals && assessment.vitals) {
    const vitals = [];
    if (assessment.vitals.hr) vitals.push(`HR: ${assessment.vitals.hr}`);
    if (assessment.vitals.bp) vitals.push(`BP: ${assessment.vitals.bp}`);
    if (assessment.vitals.rr) vitals.push(`RR: ${assessment.vitals.rr}`);
    if (assessment.vitals.temp) vitals.push(`Temp: ${assessment.vitals.temp}`);
    if (assessment.vitals.o2sat) vitals.push(`O2 Sat: ${assessment.vitals.o2sat}`);
    if (vitals.length > 0) {
      sections.push(`**Vital Signs:**\n${vitals.join(' | ')}`);
    }
  }
  if (assessment.clinicalSignificance) {
    sections.push(`**Clinical Significance:**\n${assessment.clinicalSignificance}`);
  }
  
  return sections.join('\n\n');
}

export async function fetchConditionStats() {
  try {
    // Fetch all conditions to count by category dynamically
    const { data: conditions, count: total } = await supabase
      .from('medical_conditions')
      .select('category', { count: 'exact' });

    // Count conditions per category
    const categoryCounts: Record<string, number> = {};
    conditions?.forEach(condition => {
      const cat = condition.category || 'unknown';
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    });

    return {
      total: total || 0,
      categoryCounts,
    };
  } catch (error) {
    console.error('Error fetching condition stats:', error);
    return { total: 0, categoryCounts: {} };
  }
}

// ============================================
// PHYSICAL ASSESSMENTS API
// ============================================

export async function fetchPhysicalAssessments(filters?: {
  search?: string;
  system?: string;
  type?: string;
}) {
  try {
    let query = supabase
      .from('physical_assessments')
      .select('*');

    if (filters?.search) {
      query = query.ilike('name', `%${filters.search}%`);
    }

    if (filters?.system && filters.system !== 'all') {
      query = query.eq('system', filters.system);
    }

    if (filters?.type && filters.type !== 'all') {
      query = query.eq('type', filters.type);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;

    // Transform to match expected format
    return data?.map(assessment => ({
      id: assessment.id,
      name: assessment.name,
      system: assessment.system,
      type: assessment.type,
      description: assessment.description || '',
      findings: assessment.findings || [],
      includeVitals: assessment.include_vitals || false,
      vitals: assessment.include_vitals ? {
        hr: assessment.hr || '',
        bp: assessment.bp || '',
        rr: assessment.rr || '',
        temp: assessment.temp || '',
        o2sat: assessment.o2sat || '',
      } : undefined,
      generalAppearance: assessment.general_appearance || '',
      inspection: assessment.inspection || '',
      palpation: assessment.palpation || '',
      percussion: assessment.percussion || '',
      auscultation: assessment.auscultation || '',
      clinicalSignificance: assessment.clinical_significance || '',
      differentialConsiderations: assessment.differential_considerations || '',
      recommendedActions: assessment.recommended_actions || '',
      teachingPoints: assessment.teaching_points || '',
      usedBy: assessment.used_by || 0,
    })) || [];
  } catch (error) {
    console.error('Error fetching assessments:', error);
    toast.error('Failed to load physical assessments');
    return [];
  }
}

export async function fetchAssessmentStats() {
  try {
    const { count: total } = await supabase
      .from('physical_assessments')
      .select('*', { count: 'exact', head: true });

    const { count: cardiovascular } = await supabase
      .from('physical_assessments')
      .select('*', { count: 'exact', head: true })
      .eq('system', 'cardiovascular');

    const { count: respiratory } = await supabase
      .from('physical_assessments')
      .select('*', { count: 'exact', head: true })
      .eq('system', 'respiratory');

    const { count: neurological } = await supabase
      .from('physical_assessments')
      .select('*', { count: 'exact', head: true })
      .eq('system', 'neurological');

    return {
      total: total || 0,
      cardiovascular: cardiovascular || 0,
      respiratory: respiratory || 0,
      neurological: neurological || 0,
      other: (total || 0) - (cardiovascular || 0) - (respiratory || 0) - (neurological || 0),
    };
  } catch (error) {
    console.error('Error fetching assessment stats:', error);
    return { total: 0, cardiovascular: 0, respiratory: 0, neurological: 0, other: 0 };
  }
}

// ============================================
// PHYSICAL ASSESSMENTS CRUD API
// ============================================

export async function createPhysicalAssessment(assessmentData: {
  name: string;
  system: string;
  type: string;
  description?: string;
  include_vitals?: boolean;
  bp?: string;
  hr?: string;
  rr?: string;
  temp?: string;
  o2sat?: string;
  general_appearance?: string;
  inspection?: string;
  palpation?: string;
  percussion?: string;
  auscultation?: string;
  findings?: string[];
  clinical_significance?: string;
  differential_considerations?: string;
  recommended_actions?: string;
  teaching_points?: string;
}): Promise<{ success: boolean; error?: string; id?: string }> {
  try {
    const { data, error } = await supabase
      .from('physical_assessments')
      .insert({
        name: assessmentData.name,
        system: assessmentData.system,
        type: assessmentData.type,
        description: assessmentData.description || null,
        include_vitals: assessmentData.include_vitals || false,
        bp: assessmentData.bp || null,
        hr: assessmentData.hr || null,
        rr: assessmentData.rr || null,
        temp: assessmentData.temp || null,
        o2sat: assessmentData.o2sat || null,
        general_appearance: assessmentData.general_appearance || null,
        inspection: assessmentData.inspection || null,
        palpation: assessmentData.palpation || null,
        percussion: assessmentData.percussion || null,
        auscultation: assessmentData.auscultation || null,
        findings: assessmentData.findings || [],
        clinical_significance: assessmentData.clinical_significance || null,
        differential_considerations: assessmentData.differential_considerations || null,
        recommended_actions: assessmentData.recommended_actions || null,
        teaching_points: assessmentData.teaching_points || null,
      })
      .select()
      .single();

    if (error) throw error;

    toast.success('Physical assessment created successfully');
    return { success: true, id: data.id };
  } catch (error: any) {
    console.error('❌ Create assessment failed:', error);
    toast.error(error.message || 'Failed to create physical assessment');
    return { success: false, error: error.message };
  }
}

export async function updatePhysicalAssessment(
  id: string,
  assessmentData: {
    name?: string;
    system?: string;
    type?: string;
    description?: string;
    include_vitals?: boolean;
    bp?: string;
    hr?: string;
    rr?: string;
    temp?: string;
    o2sat?: string;
    general_appearance?: string;
    inspection?: string;
    palpation?: string;
    percussion?: string;
    auscultation?: string;
    findings?: string[];
    clinical_significance?: string;
    differential_considerations?: string;
    recommended_actions?: string;
    teaching_points?: string;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const updateData: Record<string, any> = {};
    
    if (assessmentData.name !== undefined) updateData.name = assessmentData.name;
    if (assessmentData.system !== undefined) updateData.system = assessmentData.system;
    if (assessmentData.type !== undefined) updateData.type = assessmentData.type;
    if (assessmentData.description !== undefined) updateData.description = assessmentData.description || null;
    if (assessmentData.include_vitals !== undefined) updateData.include_vitals = assessmentData.include_vitals;
    if (assessmentData.bp !== undefined) updateData.bp = assessmentData.bp || null;
    if (assessmentData.hr !== undefined) updateData.hr = assessmentData.hr || null;
    if (assessmentData.rr !== undefined) updateData.rr = assessmentData.rr || null;
    if (assessmentData.temp !== undefined) updateData.temp = assessmentData.temp || null;
    if (assessmentData.o2sat !== undefined) updateData.o2sat = assessmentData.o2sat || null;
    if (assessmentData.general_appearance !== undefined) updateData.general_appearance = assessmentData.general_appearance || null;
    if (assessmentData.inspection !== undefined) updateData.inspection = assessmentData.inspection || null;
    if (assessmentData.palpation !== undefined) updateData.palpation = assessmentData.palpation || null;
    if (assessmentData.percussion !== undefined) updateData.percussion = assessmentData.percussion || null;
    if (assessmentData.auscultation !== undefined) updateData.auscultation = assessmentData.auscultation || null;
    if (assessmentData.findings !== undefined) updateData.findings = assessmentData.findings;
    if (assessmentData.clinical_significance !== undefined) updateData.clinical_significance = assessmentData.clinical_significance || null;
    if (assessmentData.differential_considerations !== undefined) updateData.differential_considerations = assessmentData.differential_considerations || null;
    if (assessmentData.recommended_actions !== undefined) updateData.recommended_actions = assessmentData.recommended_actions || null;
    if (assessmentData.teaching_points !== undefined) updateData.teaching_points = assessmentData.teaching_points || null;

    const { error } = await supabase
      .from('physical_assessments')
      .update(updateData)
      .eq('id', id);

    if (error) throw error;

    toast.success('Physical assessment updated successfully');
    return { success: true };
  } catch (error: any) {
    console.error('❌ Update assessment failed:', error);
    toast.error(error.message || 'Failed to update physical assessment');
    return { success: false, error: error.message };
  }
}

export async function deletePhysicalAssessment(id: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('physical_assessments')
      .delete()
      .eq('id', id);

    if (error) throw error;

    toast.success('Physical assessment deleted successfully');
    return { success: true };
  } catch (error: any) {
    console.error('❌ Delete assessment failed:', error);
    toast.error(error.message || 'Failed to delete physical assessment');
    return { success: false, error: error.message };
  }
}

export async function fetchPhysicalAssessmentById(id: string) {
  try {
    const { data, error } = await supabase
      .from('physical_assessments')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      id: data.id,
      name: data.name,
      system: data.system,
      type: data.type,
      description: data.description || '',
      includeVitals: data.include_vitals || false,
      bp: data.bp || '',
      hr: data.hr || '',
      rr: data.rr || '',
      temp: data.temp || '',
      o2sat: data.o2sat || '',
      generalAppearance: data.general_appearance || '',
      inspection: data.inspection || '',
      palpation: data.palpation || '',
      percussion: data.percussion || '',
      auscultation: data.auscultation || '',
      findings: data.findings || [],
      clinicalSignificance: data.clinical_significance || '',
      differentialConsiderations: data.differential_considerations || '',
      recommendedActions: data.recommended_actions || '',
      teachingPoints: data.teaching_points || '',
    };
  } catch (error) {
    console.error('Error fetching assessment:', error);
    toast.error('Failed to load assessment');
    return null;
  }
}

// ============================================
// COMPANIES API
// ============================================

export async function fetchCompanies(filters?: {
  search?: string;
  industry?: string;
  status?: string;
  tenant_id?: string; // For white-label data isolation
}) {
  try {
    let query = supabase
      .from('companies')
      .select('*');

    // Filter by tenant for white-label (master tenant sees all)
    if (filters?.tenant_id && !isMasterTenant(filters.tenant_id) && tableHasTenantId('companies')) {
      query = query.eq('tenant_id', filters.tenant_id);
    }

    if (filters?.search) {
      query = query.ilike('name', `%${filters.search}%`);
    }

    if (filters?.industry && filters.industry !== 'all') {
      query = query.eq('industry', filters.industry);
    }

    if (filters?.status && filters.status !== 'all') {
      const isActive = filters.status === 'active';
      query = query.eq('is_active', isActive);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;

    // Transform to match UI expectations
    const companies = data?.map(company => ({
      ...company,
      status: company.is_active ? 'active' : 'inactive',
      industry: company.industry?.toLowerCase() || 'pharmaceutical',
      logo: company.logo_url,
      website: company.website || '',
      created_at: new Date(company.created_at).toLocaleDateString(),
    })) || [];

    return companies;
  } catch (error) {
    console.error('Error fetching companies:', error);
    toast.error('Failed to load companies');
    return [];
  }
}

export async function fetchCompanyById(id: string) {
  try {
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .select('*')
      .eq('id', id)
      .single();

    if (companyError) throw companyError;

    // Get user count for this company
    const { count: userCount } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', id);

    // Get simulation stats for this company's users
    const { data: users } = await supabase
      .from('profiles')
      .select('id')
      .eq('company_id', id);

    const userIds = users?.map(u => u.id) || [];

    let totalSimulations = 0;
    let avgScore: number | null = null;

    if (userIds.length > 0) {
      const { data: evaluations } = await supabase
        .from('simulation_evaluations')
        .select('overall_score')
        .in('user_id', userIds);

      totalSimulations = evaluations?.length || 0;
      if (evaluations && evaluations.length > 0) {
        avgScore = averageScore(evaluations);
      }
    }

    return {
      ...company,
      total_users: userCount || 0,
      active_users: userCount || 0, // We can refine this later
      total_simulations: totalSimulations,
      avg_team_score: avgScore,
    };
  } catch (error) {
    console.error('Error fetching company:', error);
    toast.error('Failed to load company details');
    return null;
  }
}

export async function deleteCompany(companyId: string) {
  try {
    
    // Check if there are any users assigned to this company
    const { count: userCount } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId);
    
    if (userCount && userCount > 0) {
      throw new Error(`Cannot delete: ${userCount} users are still assigned to this company. Please reassign or remove them first.`);
    }
    
    // Check for agents assigned to this company
    const { count: agentCount } = await supabase
      .from('simulation_agents')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId);
    
    if (agentCount && agentCount > 0) {
      throw new Error(`Cannot delete: ${agentCount} agents are still assigned to this company. Please delete them first.`);
    }
    
    // Delete the company
    const { error } = await supabase
      .from('companies')
      .delete()
      .eq('id', companyId);
    
    if (error) {
      if (error.code === '42501') {
        throw new Error('Permission denied. Only administrators can delete companies.');
      }
      throw error;
    }
    
    toast.success('Company deleted successfully');
    return true;
  } catch (error: any) {
    console.error('❌ Delete company failed:', error);
    toast.error(error.message || 'Failed to delete company');
    throw error;
  }
}

export async function updateCompany(
  companyId: string,
  companyData: {
    name?: string;
    industry?: string;
    logo_url?: string;
    banner_image_url?: string;
    website?: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    is_active?: boolean;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('companies')
      .update(companyData)
      .eq('id', companyId);

    if (error) {
      if (error.code === '42501') {
        throw new Error('Permission denied. Only administrators can update companies.');
      }
      throw error;
    }

    toast.success('Company updated successfully');
    return { success: true };
  } catch (error: any) {
    console.error('❌ Update company failed:', error);
    toast.error(error.message || 'Failed to update company');
    return { success: false, error: error.message };
  }
}

export async function fetchCompanyStats(tenantId?: string) {
  try {
    // Build company query - filter by tenant if provided
    let companyQuery = supabase
      .from('companies')
      .select('id', { count: 'exact', head: true });
    
    if (tenantId) {
      companyQuery = companyQuery.eq('tenant_id', tenantId);
    }
    
    const { count: totalCompanies } = await companyQuery;

    // Get company IDs for this tenant to filter related data
    let companyIdsQuery = supabase.from('companies').select('id');
    if (tenantId) {
      companyIdsQuery = companyIdsQuery.eq('tenant_id', tenantId);
    }
    const { data: companiesData } = await companyIdsQuery;
    const companyIds = companiesData?.map(c => c.id) || [];

    // Count active users in these companies
    let activeUsersCount = 0;
    if (companyIds.length > 0) {
      const { count } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .in('company_id', companyIds);
      activeUsersCount = count || 0;
    } else if (!tenantId) {
      // No tenant filter - count all profiles
      const { count } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true });
      activeUsersCount = count || 0;
    }

    // Count simulations for users in these companies
    let totalSims = 0;
    let avgScore: number | null = null;
    
    if (companyIds.length > 0) {
      // Get user IDs from these companies
      const { data: usersData } = await supabase
        .from('profiles')
        .select('id')
        .in('company_id', companyIds);
      const userIds = usersData?.map(u => u.id) || [];
      
      if (userIds.length > 0) {
        const { count: simsCount } = await supabase
          .from('simulation_evaluations')
          .select('id', { count: 'exact', head: true })
          .in('user_id', userIds);
        totalSims = simsCount || 0;

        const { data: scores } = await supabase
          .from('simulation_evaluations')
          .select('overall_score')
          .in('user_id', userIds);
        
        avgScore = averageScore(scores);
      }
    } else if (!tenantId) {
      // No tenant filter - count all
      const { count: simsCount } = await supabase
        .from('simulation_evaluations')
        .select('id', { count: 'exact', head: true });
      totalSims = simsCount || 0;

      const { data: scores } = await supabase
        .from('simulation_evaluations')
        .select('overall_score');
      
      avgScore = averageScore(scores);
    }

    return {
      total: totalCompanies || 0,
      activeUsers: activeUsersCount,
      totalSims,
      avgScore
    };
  } catch (error) {
    console.error('Error fetching company stats:', error);
    return { total: 0, activeUsers: 0, totalSims: 0, avgScore: 0 };
  }
}

// ============================================
// SIMULATION EVALUATIONS API
// ============================================

export async function fetchUserSimulations(userId: string, tenantId?: string) {
  try {
    // Get the authenticated user
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) {
      console.error('❌ No authenticated user');
      return [];
    }

    // Step 1: Fetch agents - RLS handles tenant isolation
    // We then filter client-side to only show:
    // - System agents (is_system=true) - visible to all tenants
    // - Current tenant's agents (tenant_id matches URL tenant)
    let agentsQuery = supabase
      .from('simulation_agents')
      .select('*')
      .eq('status', 'active')
      .neq('is_template', true);

    const { data: allAgents, error: agentsError } = await agentsQuery.order('created_at', { ascending: false });
    
    if (agentsError) throw agentsError;
    if (!allAgents || allAgents.length === 0) return [];

    // CRITICAL: Filter to current tenant only (tenant isolation)
    // Users may be admin of multiple tenants, but should only see current tenant's agents
    const filteredByTenant = allAgents.filter(agent => {
      // System agents are visible to everyone
      if (agent.is_system === true) return true;
      // For white-label tenants, only show agents from that specific tenant
      if (tenantId && tenantId !== MASTER_TENANT_ID) {
        return agent.tenant_id === tenantId;
      }
      // Master tenant sees all (admin view)
      return true;
    });

    if (filteredByTenant.length === 0) return [];

    // Step 1.5: Get user's specific assignments from agent_assignments table
    const { data: userAssignments } = await supabase
      .from('agent_assignments')
      .select('agent_id')
      .eq('user_id', userId);

    const assignedAgentIds = new Set(userAssignments?.map(a => a.agent_id) || []);

    // Filter agents: show if assign_to='all'/null OR user is specifically assigned
    const agents = filteredByTenant.filter(agent => {
      // Global agents (assign_to = 'all' or null) are visible to everyone
      if (agent.assign_to === 'all' || agent.assign_to === null) {
        return true;
      }
      // Specific assignment agents only visible if user is in agent_assignments
      if (agent.assign_to === 'specific') {
        return assignedAgentIds.has(agent.id);
      }
      return true; // Default to showing
    });

    if (agents.length === 0) return [];

    const companyIds = Array.from(new Set(
      agents.map(agent => agent.company_id).filter(Boolean) as string[]
    ));
    const coachingTemplateIds = Array.from(new Set(
      agents.map(agent => agent.coaching_template_id).filter(Boolean) as string[]
    ));
    const editableUnitIds = Array.from(new Set(
      agents
        .map(agent => (agent as { editable_unit_id?: string | null }).editable_unit_id)
        .filter((unitId): unitId is string => Boolean(unitId))
    ));
    type UnitLookupResult = {
      data: { id: string; name: string }[] | null;
      error: unknown;
    };
    type UnitLookupClient = {
      from: (table: 'organisation_units') => {
        select: (columns: 'id, name') => {
          in: (column: 'id', values: string[]) => PromiseLike<UnitLookupResult>;
        };
      };
    };
    const enterpriseDb = supabase as unknown as UnitLookupClient;

    const [companiesResult, coachingTemplatesResult, unitsResult] = await Promise.all([
      companyIds.length
        ? supabase.from('companies').select('id, name').in('id', companyIds)
        : Promise.resolve({ data: [] as { id: string; name: string }[], error: null }),
      coachingTemplateIds.length
        ? supabase.from('coaching_templates').select('id, name, output_document').in('id', coachingTemplateIds)
        : Promise.resolve({ data: [] as { id: string; name: string; output_document: string }[], error: null }),
      editableUnitIds.length
        ? enterpriseDb
            .from('organisation_units')
            .select('id, name')
            .in('id', editableUnitIds)
        : Promise.resolve({ data: [] as { id: string; name: string }[], error: null }),
    ]);

    if (companiesResult.error) {
      console.warn('Unable to load simulation card company names:', companiesResult.error);
    }
    if (coachingTemplatesResult.error) {
      console.warn('Unable to load simulation card output templates:', coachingTemplatesResult.error);
    }
    if (unitsResult.error) {
      console.warn('Unable to load simulation card unit names:', unitsResult.error);
    }

    const companyNameById = new Map(
      (companiesResult.data || []).map(company => [company.id, company.name])
    );
    const coachingTemplateById = new Map(
      (coachingTemplatesResult.data || []).map(template => [template.id, template])
    );
    const unitNameById = new Map<string, string>(
      (unitsResult.data || []).map((unit: { id: string; name: string }) => [unit.id, unit.name])
    );

    // Step 2: Fetch user's evaluations (using auth_user_id for RLS compliance)
    const { data: evaluations } = await supabase
      .from('simulation_evaluations')
      .select('simulation_id, overall_score, evaluated_at, is_submitted')
      .eq('auth_user_id', authUser.id)
      .is('deleted_at', null)
      .order('overall_score', { ascending: false });

    // Create map: agent_id -> { bestScore, sessionCount, hasSubmitted, avgScore }
    const evalMap = new Map<string, { bestScore: number | null; sessions: number; hasSubmitted: boolean; scoreTotal: number; scoredSessions: number }>();
    evaluations?.forEach(e => {
      const agentId = e.simulation_id;
      if (!agentId) return;
      const score = e.overall_score;
      
      const existing = evalMap.get(agentId);
      if (existing) {
        existing.sessions++;
        if (isScored(score) && (existing.bestScore === null || score > existing.bestScore)) {
          existing.bestScore = score;
        }
        if (isScored(score)) {
          existing.scoreTotal += score;
          existing.scoredSessions++;
        }
        // Track if ANY evaluation is submitted
        if (e.is_submitted) {
          existing.hasSubmitted = true;
        }
      } else {
        evalMap.set(agentId, {
          bestScore: score,
          sessions: 1,
          hasSubmitted: e.is_submitted === true,
          scoreTotal: isScored(score) ? score : 0,
          scoredSessions: isScored(score) ? 1 : 0
        });
      }
    });

    // Step 3: Map agents with user's progress
    return agents.map(agent => {
      const agentRecord = agent as any;
      const userProgress = evalMap.get(agent.id);
      const bestScore = userProgress?.bestScore || 0;
      const sessions = userProgress?.sessions || 0;
      const hasSubmitted = userProgress?.hasSubmitted || false;
      const goalScore = agent.goal_score || 85;
      const userAverageScore = userProgress?.scoredSessions
        ? Math.round(userProgress.scoreTotal / userProgress.scoredSessions)
        : 0;

      // Determine status based on submission, not score threshold
      // - completed: has at least one submitted evaluation
      // - in-progress: has evaluations but none submitted
      // - not-started: no evaluations at all
      let status: 'not-started' | 'in-progress' | 'completed' = 'not-started';
      if (sessions > 0) {
        status = hasSubmitted ? 'completed' : 'in-progress';
      }

      // Build a meaningful description from available fields (strip HTML tags)
      const strip = (s: string) => s.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
      const descriptionParts: string[] = [];
      if (agent.scenario_context) descriptionParts.push(strip(agent.scenario_context));
      else if (agent.presenting_complaint && agent.type === 'patient') {
        descriptionParts.push(`Presenting with: ${strip(agent.presenting_complaint)}`);
      }
      if (agent.occupation && !descriptionParts.length) descriptionParts.push(strip(agent.occupation));
      const description = descriptionParts.join('. ') || '';
      const coachingTemplate = agent.coaching_template_id
        ? coachingTemplateById.get(agent.coaching_template_id)
        : null;
      const outputItems: string[] = [];
      if (coachingTemplate?.output_document) {
        outputItems.push(strip(coachingTemplate.output_document));
      } else if (coachingTemplate?.name) {
        outputItems.push(strip(coachingTemplate.name));
      }

      const rubric = agent.evaluation_rubric;
      const hasEvaluationRubric = Array.isArray(rubric)
        ? rubric.length > 0
        : Boolean(rubric && typeof rubric === 'object' && Object.keys(rubric as Record<string, unknown>).length > 0);
      if (hasEvaluationRubric) {
        outputItems.push('Evaluation rubric');
      }

      return {
        id: agent.id,
        name: agent.name,
        title: agent.title || agent.occupation || '',
        description,
        // Learner cards must only present explicitly configured card content.
        // Falling back to organisation/type/scenario fields made internal data
        // such as "Uniting care" and "customer" look like authored headings.
        roleplayFocus: agentRecord.roleplay_focus || '',
        briefDescription: agentRecord.brief_description || '',
        outputItems: Array.from(new Set(outputItems)),
        companyName: agent.company_id ? companyNameById.get(agent.company_id) || '' : '',
        unitName: agentRecord.editable_unit_id
          ? unitNameById.get(agentRecord.editable_unit_id)
            || agent.name.match(/\s+—\s+([^—]+)$/)?.[1]?.trim()
            || ''
          : '',
        specialty: agent.specialty || '',
        type: agent.type,
        category: (agent as any).agent_category || null,
        coaching_style: agent.coaching_style || null,
        initials: agent.initials || agent.name.substring(0, 2).toUpperCase(),
        goalScore: goalScore,
        userBestScore: bestScore > 0 ? bestScore : undefined,
        status,
        sessions,
        sessionsCompleted: agent.completions || sessions,
        averageScore: agent.avg_score ? Math.round(agent.avg_score) : userAverageScore,
        estimatedDuration: agent.estimated_duration || '15-20',
        difficulty: agent.difficulty || 'intermediate',
        imageUrl: agent.image_url,
        avatarUrl: agent.image_url,
        platform: agent.platform,
        experience: agent.experience,
        location: agent.location,
        lastAttempt: undefined,
      };
    });
  } catch (error) {
    console.error('Error fetching user simulations:', error);
    toast.error('Failed to load your simulations');
    return [];
  }
}

// ============================================
// DASHBOARD STATS API
// ============================================

export async function fetchUserDashboardStats(userId: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        totalSimulations: 0,
        averageScore: 0,
        bestScore: 0,
      };
    }

    // Fetch all user's simulations (submitted only)
    const { data: evaluations, error } = await supabase
      .from('simulation_evaluations')
      .select('overall_score')
      .eq('auth_user_id', user.id)
      .eq('is_submitted', true);

    if (error) throw error;

    const totalSimulations = evaluations?.length || 0;
    const scores = (evaluations ?? []).map(e => e.overall_score).filter(isScored);
    
    const averageScore = scores.length > 0 
      ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)
      : null;
    
    const bestScore = scores.length > 0 
      ? Math.max(...scores)
      : null;

    return {
      totalSimulations,
      averageScore,
      bestScore,
    };
  } catch (error) {
    console.error('Error fetching user dashboard stats:', error);
    return {
      totalSimulations: 0,
      averageScore: 0,
      bestScore: 0,
    };
  }
}

// ============================================
// ADMIN STATS API
// ============================================

interface ProgressQueryOptions {
  includeCpd?: boolean;
}

export async function fetchAdminDashboardStats(
  tenantId?: string,
  { includeCpd = true }: ProgressQueryOptions = {},
) {
  try {
    const filterByTenant = !isMasterTenant(tenantId);
    
    let totalUsers = 0;
    
    if (filterByTenant && tenantId) {
      // Use RPC for consistent counting with User Management page
      const { data: rpcUsers, error: rpcError } = await supabase.rpc('get_tenant_users_with_admins', {
        p_tenant_id: tenantId
      });
      
      if (rpcError) {
        console.error('RPC error in dashboard stats:', rpcError);
        // Fallback: count profiles with this tenant_id + tenant_admins
        const { data: tenantProfiles } = await supabase
          .from('profiles')
          .select('id, auth_user_id')
          .eq('tenant_id', tenantId);
        
        const { data: tenantAdmins } = await supabase
          .from('tenant_admins')
          .select('user_id')
          .eq('tenant_id', tenantId);
        
        const adminUserIds = tenantAdmins?.map(ta => ta.user_id).filter(Boolean) || [];
        const { data: adminProfiles } = adminUserIds.length > 0 
          ? await supabase.from('profiles').select('id, auth_user_id').in('auth_user_id', adminUserIds)
          : { data: [] };
        
        const allUserIds = new Set<string>();
        tenantProfiles?.forEach(p => allUserIds.add(p.id));
        adminProfiles?.forEach(p => allUserIds.add(p.id));
        
        totalUsers = allUserIds.size;
      } else {
        // RPC success - count returned users
        totalUsers = rpcUsers?.length || 0;
      }
    } else {
      // Master tenant: count all profiles
      const { count } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
      totalUsers = count || 0;
    }

    // Fetch total companies (filtered by tenant for white-label)
    let companiesQuery = supabase.from('companies').select('*', { count: 'exact', head: true });
    if (filterByTenant && tenantId && tableHasTenantId('companies')) {
      companiesQuery = companiesQuery.eq('tenant_id', tenantId);
    }
    const { count: totalCompanies } = await companiesQuery;

    // Fetch total simulations (filtered by tenant for white-label)
    // Note: simulation_evaluations tenant_id will be added via migration
    let simulationsQuery = supabase
      .from('simulation_evaluations')
      .select('*', { count: 'exact', head: true })
      .eq('is_submitted', true);
    if (filterByTenant && tenantId && tableHasTenantId('simulation_evaluations')) {
      simulationsQuery = simulationsQuery.eq('tenant_id', tenantId);
    }
    const { count: totalSimulations } = await simulationsQuery;

    // Fetch average score (filtered by tenant for white-label)
    let scoresQuery = supabase
      .from('simulation_evaluations')
      .select('overall_score')
      .eq('is_submitted', true)
      .not('overall_score', 'is', null);
    if (filterByTenant && tenantId && tableHasTenantId('simulation_evaluations')) {
      scoresQuery = scoresQuery.eq('tenant_id', tenantId);
    }
    const { data: evaluations } = await scoresQuery;

    const scores = (evaluations ?? []).map(e => e.overall_score).filter(isScored);
    const avgPlatformScore = scores.length > 0 
      ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length)
      : null;

    // Fetch agents with type breakdown (filtered by tenant for white-label)
    let agentsQuery = supabase
      .from('simulation_agents')
      .select('type, status, tenant_id')
      .or('status.eq.active,status.is.null');
    if (filterByTenant && tenantId && tableHasTenantId('simulation_agents')) {
      agentsQuery = agentsQuery.eq('tenant_id', tenantId);
    }
    const { data: agents } = await agentsQuery;

    const totalAgents = agents?.length || 0;
    const patientAgents = agents?.filter(a => a.type === 'patient').length || 0;
    const customerAgents = agents?.filter(a => a.type === 'customer').length || 0;
    const examinerAgents = agents?.filter(a => a.type === 'examiner').length || 0;

    // CPD is optional per tenant. Disabled tenants must not query or display it.
    let cpdPointsIssued = 0;
    if (includeCpd) {
      const { data: cpdData } = await supabase
        .from('user_cpd_points')
        .select('points, user_id');
      cpdPointsIssued = cpdData?.reduce((sum, row) => sum + (row.points || 0), 0) || 0;
    }

    // Fetch active users (users with activity in last 30 days)
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    let activeUsersQuery = supabase
      .from('simulation_evaluations')
      .select('auth_user_id')
      .gte('created_at', thirtyDaysAgo);
    if (filterByTenant && tenantId && tableHasTenantId('simulation_evaluations')) {
      activeUsersQuery = activeUsersQuery.eq('tenant_id', tenantId);
    }
    const { data: recentEvaluations } = await activeUsersQuery;
    const activeUsers = new Set(recentEvaluations?.map(e => e.auth_user_id).filter(Boolean)).size;

    // Fetch total tenants (only for master tenant - super admin view)
    let totalTenants = 0;
    if (!filterByTenant) {
      const { count: tenantCount } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true });
      totalTenants = tenantCount || 0;
    }

    return {
      totalUsers: totalUsers || 0,
      activeUsers,
      totalCompanies: totalCompanies || 0,
      totalSimulations: totalSimulations || 0,
      avgPlatformScore,
      totalAgents,
      patientAgents,
      customerAgents,
      examinerAgents,
      cpdPointsIssued,
      totalTenants,
    };
  } catch (error) {
    console.error('Error fetching admin dashboard stats:', error);
    return {
      totalUsers: 0,
      activeUsers: 0,
      totalCompanies: 0,
      totalSimulations: 0,
      avgPlatformScore: 0,
      totalAgents: 0,
      patientAgents: 0,
      customerAgents: 0,
      examinerAgents: 0,
      cpdPointsIssued: 0,
      totalTenants: 0,
    };
  }
}

// ============================================
// COMPANY ADMIN API
// ============================================

export async function fetchTeamPerformance(companyId: string) {
  try {
    const { data, error } = await supabase
      .from('team_performance_cache')
      .select('*')
      .eq('company_id', companyId)
      .order('average_score', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(member => ({
      id: member.user_id,
      name: member.user_name,
      email: member.user_email,
      sessions: member.total_sessions,
      bestScore: member.best_score,
      avgScore: member.average_score,
      worstScore: member.worst_score,
      lastSession: member.last_session_date,
    })) || [];
  } catch (error) {
    console.error('Error fetching team performance:', error);
    return [];
  }
}

export function exportToCSV(data: any[], filename: string) {
  if (data.length === 0) {
    toast.error('No data to export');
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(h => {
      const value = row[h];
      // Escape values that contain commas
      return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
    }).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
  toast.success('Data exported successfully');
}

export async function fetchCompanyDashboardStats(companyId: string) {
  try {
    // Get company users
    const { data: companyUsers } = await supabase
      .from('profiles')
      .select('id, auth_user_id')
      .eq('company_id', companyId);
    
    const userIds = companyUsers?.map(u => u.id) || [];
    const authUserIds = companyUsers?.map(u => u.auth_user_id).filter(Boolean) || [];

    // Get company's total simulations (submitted only)
    const { count: totalSims } = await supabase
      .from('simulation_evaluations')
      .select('*', { count: 'exact', head: true })
      .in('user_id', userIds)
      .eq('is_submitted', true);

    // Get company's scores
    const { data: scores } = await supabase
      .from('simulation_evaluations')
      .select('overall_score')
      .in('user_id', userIds)
      .eq('is_submitted', true)
      .not('overall_score', 'is', null);

    const avgScore = averageScore(scores);

    // Count active agents available to company
    const { count: availableAgents } = await supabase
      .from('simulation_agents')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active')
      .or(`company_id.is.null,company_id.eq.${companyId}`);

    return {
      teamMembers: companyUsers?.length || 0,
      totalSimulations: totalSims || 0,
      teamAverageScore: avgScore,
      availableAgents: availableAgents || 0,
    };
  } catch (error) {
    console.error('Error fetching company stats:', error);
    return {
      teamMembers: 0,
      totalSimulations: 0,
      teamAverageScore: 0,
      availableAgents: 0,
    };
  }
}

// ============================================
// PROGRESS/CPD QUERIES
// ============================================

export async function fetchUserProgressData(
  userId: string,
  { includeCpd = true }: ProgressQueryOptions = {},
) {
  try {
    // Get auth user for fallback queries
    const { data: { user: authUser } } = await supabase.auth.getUser();
    
    // Get all evaluations - try by user_id first, then by auth_user_id
    let evaluations = null;
    
    const { data: evalByUserId } = await supabase
      .from('simulation_evaluations')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('evaluated_at', { ascending: true });
    
    if (evalByUserId && evalByUserId.length > 0) {
      evaluations = evalByUserId;
    } else if (authUser) {
      // Try with auth_user_id as fallback
      const { data: evalByAuthId } = await supabase
        .from('simulation_evaluations')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .is('deleted_at', null)
        .order('evaluated_at', { ascending: true });
      evaluations = evalByAuthId;
    }

    const totalSimulations = evaluations?.length || 0;
    const avgScore = averageScore(evaluations);

    // Get coaching sessions data - also try both user_id and auth_user_id
    let coachingSessions = null;
    
    const { data: coachByUserId } = await supabase
      .from('coaching_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'completed')
      .order('session_end', { ascending: true });
    
    if (coachByUserId && coachByUserId.length > 0) {
      coachingSessions = coachByUserId;
    } else if (authUser) {
      const { data: coachByAuthId } = await supabase
        .from('coaching_sessions')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .eq('status', 'completed')
        .order('session_end', { ascending: true });
      coachingSessions = coachByAuthId;
    }

    const totalCoaching = coachingSessions?.length || 0;
    const avgReadinessScore = coachingSessions?.length && coachingSessions.some(s => s.readiness_score)
      ? Math.round(
          coachingSessions
            .filter(s => s.readiness_score)
            .reduce((sum, s) => sum + (s.readiness_score || 0), 0) / 
          coachingSessions.filter(s => s.readiness_score).length
        )
      : 0;

    // Get streak data - try both user_id and auth_user_id
    let streakData = null;
    const { data: streakByUserId } = await supabase
      .from('user_streaks')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (streakByUserId) {
      streakData = streakByUserId;
    } else if (authUser) {
      // Try with auth_user_id as fallback
      const { data: streakByAuthId } = await supabase
        .from('user_streaks')
        .select('*')
        .eq('user_id', authUser.id)
        .maybeSingle();
      streakData = streakByAuthId;
    }

    const currentStreak = streakData?.current_streak || 0;
    const longestStreak = streakData?.longest_streak || 0;

    // Get CPD points only when the tenant has the feature enabled.
    let cpdPoints = 0;
    if (includeCpd) {
      let cpdData = null;
      const { data: cpdByUserId } = await supabase
        .from('user_cpd_points')
        .select('points')
        .eq('user_id', userId);

      if (cpdByUserId && cpdByUserId.length > 0) {
        cpdData = cpdByUserId;
      } else if (authUser) {
        const { data: cpdByAuthId } = await supabase
          .from('user_cpd_points')
          .select('points')
          .eq('user_id', authUser.id);
        cpdData = cpdByAuthId;
      }

      cpdPoints = cpdData?.reduce((sum, record) => sum + record.points, 0) || 0;
    }

    // Get badges - try both user_id and auth_user_id
    let badgesData = null;
    const { data: badgesByUserId } = await supabase
      .from('user_badges')
      .select('*')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });
    
    if (badgesByUserId && badgesByUserId.length > 0) {
      badgesData = badgesByUserId;
    } else if (authUser) {
      const { data: badgesByAuthId } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', authUser.id)
        .order('earned_at', { ascending: false });
      badgesData = badgesByAuthId;
    }

    const badges = badgesData?.map(b => ({
      id: b.badge_id,
      icon: b.badge_icon,
      name: b.badge_name,
      description: b.badge_description,
      earnedAt: b.earned_at,
    })) || [];

    // Calculate trends (compare last 30 days to previous 30 days)
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    const lastMonthEvals = evaluations?.filter(e => 
      new Date(e.evaluated_at) >= thirtyDaysAgo
    ) || [];
    
    const prevMonthEvals = evaluations?.filter(e => {
      const date = new Date(e.evaluated_at);
      return date >= sixtyDaysAgo && date < thirtyDaysAgo;
    }) || [];

    const lastMonthCount = lastMonthEvals.length;
    const prevMonthCount = prevMonthEvals.length;
    const simTrend = prevMonthCount > 0 
      ? `${lastMonthCount > prevMonthCount ? '+' : ''}${lastMonthCount - prevMonthCount}`
      : '+0';

    const lastMonthAvg = scoreStatistics(lastMonthEvals).average;
    const prevMonthAvg = scoreStatistics(prevMonthEvals).average;
    const avgTrend = lastMonthAvg !== null && prevMonthAvg !== null && prevMonthAvg > 0
      ? `${lastMonthAvg > prevMonthAvg ? '+' : ''}${Math.round(((lastMonthAvg - prevMonthAvg) / prevMonthAvg) * 100)}%`
      : '+0%';

    // Coaching trends
    const lastMonthCoaching = coachingSessions?.filter(s => 
      s.session_end && new Date(s.session_end) >= thirtyDaysAgo
    ) || [];
    const prevMonthCoaching = coachingSessions?.filter(s => {
      if (!s.session_end) return false;
      const date = new Date(s.session_end);
      return date >= sixtyDaysAgo && date < thirtyDaysAgo;
    }) || [];
    const coachingTrend = prevMonthCoaching.length > 0
      ? `${lastMonthCoaching.length > prevMonthCoaching.length ? '+' : ''}${lastMonthCoaching.length - prevMonthCoaching.length}`
      : '+0';

    let cpdTrend = '+0';
    if (includeCpd) {
      const [{ data: lastMonthCpd }, { data: prevMonthCpd }] = await Promise.all([
        supabase
          .from('user_cpd_points')
          .select('points')
          .eq('user_id', userId)
          .gte('earned_at', thirtyDaysAgo.toISOString()),
        supabase
          .from('user_cpd_points')
          .select('points')
          .eq('user_id', userId)
          .gte('earned_at', sixtyDaysAgo.toISOString())
          .lt('earned_at', thirtyDaysAgo.toISOString()),
      ]);
      const lastCpdTotal = lastMonthCpd?.reduce((sum, row) => sum + row.points, 0) || 0;
      const previousCpdTotal = prevMonthCpd?.reduce((sum, row) => sum + row.points, 0) || 0;
      cpdTrend = `${lastCpdTotal > previousCpdTotal ? '+' : ''}${lastCpdTotal - previousCpdTotal}`;
    }

    // Extract skills from evaluation data
    const skillScores: Record<string, { total: number; count: number; recent: number[] }> = {};
    
    evaluations?.forEach((evaluation, index) => {
      const detailedEval = evaluation.detailed_evaluation as any;
      if (detailedEval?.categories && Array.isArray(detailedEval.categories)) {
        detailedEval.categories.forEach((category: any) => {
          if (!skillScores[category.name]) {
            skillScores[category.name] = { total: 0, count: 0, recent: [] };
          }
          skillScores[category.name].total += category.score || 0;
          skillScores[category.name].count++;
          
          // Track last 3 evaluations for trend
          if (index >= evaluations.length - 3) {
            skillScores[category.name].recent.push(category.score || 0);
          }
        });
      }
    });

    const skills = Object.entries(skillScores).map(([name, data]) => {
      const avgScoreSkill = Math.round(data.total / data.count);
      const recentAvg = data.recent.length 
        ? data.recent.reduce((sum, s) => sum + s, 0) / data.recent.length
        : avgScoreSkill;
      
      return {
        name,
        score: avgScoreSkill,
        trend: recentAvg >= avgScoreSkill ? 'up' as const : 'down' as const,
        color: avgScoreSkill >= 80 ? 'text-green-600' : avgScoreSkill >= 60 ? 'text-yellow-600' : 'text-red-600'
      };
    });

    // Prepare chart data
    const weeklyData = lastMonthEvals.slice(-7).map(e => ({
      date: new Date(e.evaluated_at).toLocaleDateString(),
      score: e.overall_score,
      goal: 85
    }));

    const monthlyData = lastMonthEvals.map(e => ({
      date: new Date(e.evaluated_at).toLocaleDateString(),
      score: e.overall_score,
      goal: 85
    }));

    const yearlyData = evaluations
      ?.filter(e => new Date(e.evaluated_at) >= new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000))
      .map(e => ({
        date: new Date(e.evaluated_at).toLocaleDateString(),
        score: e.overall_score,
        goal: 85
      })) || [];

    return {
      stats: {
        totalSimulations,
        avgScore,
        thisMonthCount: lastMonthCount,
        cpdPoints,
        cpdTarget: 60,
        currentStreak,
        longestStreak,
        trendSimulations: simTrend,
        trendAvgScore: avgTrend,
        trendCPD: cpdTrend,
        streakStatus: currentStreak > 0 ? 'active' as const : 'inactive' as const,
        badges,
        // Coaching stats
        totalCoaching,
        avgReadinessScore,
        thisMonthCoachingCount: lastMonthCoaching.length,
        trendCoaching: coachingTrend,
      },
      weeklyData,
      monthlyData,
      yearlyData,
      skills
    };
  } catch (error) {
    console.error('Error fetching progress data:', error);
    return null;
  }
}

export async function fetchRecentActivity(
  context: 'user' | 'company' | 'admin',
  limit: number = 10,
  contextId?: string,
  tenantId?: string // For white-label data isolation
) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    let query = supabase
      .from('simulation_evaluations')
      .select(`
        id,
        agent_id,
        overall_score,
        evaluated_at,
        user_id,
        user_name,
        user_email,
        agent_name
      `)
      .eq('is_submitted', true)
      .order('evaluated_at', { ascending: false })
      .limit(limit);

    // Filter by tenant for white-label (master tenant sees all)
    if (tenantId && !isMasterTenant(tenantId) && tableHasTenantId('simulation_evaluations')) {
      query = query.eq('tenant_id', tenantId);
    }

    if (context === 'user' && user) {
      query = query.eq('auth_user_id', user.id);
    } else if (context === 'company' && contextId) {
      const { data: companyUsers } = await supabase
        .from('profiles')
        .select('id')
        .eq('company_id', contextId);
      
      const userIds = companyUsers?.map(u => u.id) || [];
      if (userIds.length > 0) {
        query = query.in('user_id', userIds);
      }
    }

    const { data, error } = await query;
    if (error) throw error;

    // Get profile names for missing user names
    const userIdsWithMissingNames = (data || [])
      .filter(item => !item.user_name && item.user_id)
      .map(item => item.user_id);
    
    let profileMap = new Map<string, string>();
    if (userIdsWithMissingNames.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', userIdsWithMissingNames);
      
      profiles?.forEach(p => {
        profileMap.set(p.id, p.display_name || 'User');
      });
    }

    return data?.map(item => {
      const userName = item.user_name || profileMap.get(item.user_id) || 'Unknown User';
      return {
        id: item.id,
        userName,
        agentName: item.agent_name || 'Unknown Agent',
        score: item.overall_score,
        date: item.evaluated_at,
        status: 'completed' as const,
        userInitials: userName.split(' ').map(n => n[0]).join('').toUpperCase(),
      };
    }) || [];
  } catch (error) {
    console.error('Error fetching activity:', error);
    return [];
  }
}

// ============================================
// ADMIN DASHBOARD QUERIES
// ============================================

export async function fetchUserActivityData(days: number = 7) {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from('simulation_evaluations')
      .select('evaluated_at, user_id')
      .gte('evaluated_at', startDate.toISOString())
      .order('evaluated_at', { ascending: true });

    if (error) throw error;

    // Group by date
    const groupedData: Record<string, { users: Set<string>, simulations: number }> = {};
    
    data?.forEach(item => {
      const date = new Date(item.evaluated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!groupedData[date]) {
        groupedData[date] = { users: new Set(), simulations: 0 };
      }
      groupedData[date].users.add(item.user_id);
      groupedData[date].simulations++;
    });

    return Object.entries(groupedData).map(([date, data]) => ({
      date,
      users: data.users.size,
      simulations: data.simulations,
    }));
  } catch (error) {
    console.error('Error fetching user activity data:', error);
    return [];
  }
}

export async function fetchSimulationsBySpecialty() {
  try {
    // First get all evaluations
    const { data: evaluations, error: evalError } = await supabase
      .from('simulation_evaluations')
      .select('agent_id, overall_score');

    if (evalError) throw evalError;
    if (!evaluations) return [];

    // Get unique agent IDs
    const agentIds = [...new Set(evaluations.map(e => e.agent_id))];
    
    // Get agent details
    const { data: agents } = await supabase
      .from('simulation_agents')
      .select('id, specialty')
      .in('id', agentIds);

    const agentMap = new Map(agents?.map(a => [a.id, a.specialty || 'General']) || []);

    // Group by specialty
    const groupedData: Record<string, { completed: number, inProgress: number }> = {};
    
    evaluations.forEach(item => {
      const specialty = agentMap.get(item.agent_id) || 'General';
      if (!groupedData[specialty]) {
        groupedData[specialty] = { completed: 0, inProgress: 0 };
      }
      if (item.overall_score && item.overall_score >= 85) {
        groupedData[specialty].completed++;
      } else {
        groupedData[specialty].inProgress++;
      }
    });

    return Object.entries(groupedData).map(([specialty, data]) => ({
      specialty,
      completed: data.completed,
      inProgress: data.inProgress,
    })).sort((a, b) => (b.completed + b.inProgress) - (a.completed + a.inProgress));
  } catch (error) {
    console.error('Error fetching simulations by specialty:', error);
    return [];
  }
}

export async function fetchTopPerformingAgents(limit: number = 5) {
  try {
    // Get all active agents
    const { data: agents, error: agentError } = await supabase
      .from('simulation_agents')
      .select('id, initials, name, specialty')
      .eq('status', 'active');

    if (agentError) throw agentError;
    if (!agents) return [];

    // Get evaluations for each agent
    const agentsWithStats = await Promise.all(
      agents.map(async (agent) => {
        const { data: evaluations } = await supabase
          .from('simulation_evaluations')
          .select('overall_score, evaluated_at')
          .eq('agent_id', agent.id)
          .order('evaluated_at', { ascending: true });

        const completions = evaluations?.length || 0;
        const avgScore = averageScore(evaluations);

        // Calculate trend
        let trend: 'up' | 'down' = 'up';
        const scoredEvaluations = (evaluations ?? []).filter(e => isScored(e.overall_score));
        if (scoredEvaluations.length >= 6) {
          const recentScores = scoredEvaluations.slice(-3);
          const previousScores = scoredEvaluations.slice(-6, -3);
          const recent = recentScores.reduce((sum, e) => sum + (e.overall_score || 0), 0) / recentScores.length;
          const previous = previousScores.reduce((sum, e) => sum + (e.overall_score || 0), 0) / previousScores.length;
          trend = recent > previous ? 'up' : 'down';
        }

        return {
          initials: agent.initials,
          name: agent.name,
          specialty: agent.specialty || 'General',
          completions,
          avgScore,
          trend,
        };
      })
    );

    return agentsWithStats
      .filter(agent => agent.completions > 0 && isScored(agent.avgScore))
      .sort((a, b) => b.avgScore - a.avgScore)
      .slice(0, limit);
  } catch (error) {
    console.error('Error fetching top performing agents:', error);
    return [];
  }
}

export async function fetchRecentPlatformActivity(limit: number = 5) {
  try {
    // Fetch recent evaluations with profile fallback for user names
    const { data: evaluations } = await supabase
      .from('simulation_evaluations')
      .select(`
        evaluated_at,
        user_name,
        user_id,
        agent_name,
        overall_score
      `)
      .order('evaluated_at', { ascending: false })
      .limit(limit);

    if (!evaluations || evaluations.length === 0) return [];

    // Get profile names for users where user_name is missing
    const userIdsWithMissingNames = evaluations
      .filter(e => !e.user_name)
      .map(e => e.user_id)
      .filter(Boolean);

    let profileMap = new Map<string, string>();
    if (userIdsWithMissingNames.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', userIdsWithMissingNames);
      
      profiles?.forEach(p => {
        profileMap.set(p.id, p.display_name || 'User');
      });
    }

    const activities = evaluations.map(e => {
      const score = e.overall_score;
      const isHighScore = score >= 90;
      const userName = e.user_name || profileMap.get(e.user_id) || 'User';
      
      return {
        type: isHighScore ? 'achievement' : 'simulation',
        user: userName,
        description: isHighScore 
          ? `Achieved ${score}% in ${e.agent_name || 'simulation'}`
          : `Completed ${e.agent_name || 'simulation'}`,
        time: formatRelativeTime(e.evaluated_at),
        icon: isHighScore ? 'Target' : 'CheckCircle',
        color: isHighScore ? 'orange' : 'green',
      };
    });

    return activities;
  } catch (error) {
    console.error('Error fetching recent platform activity:', error);
    return [];
  }
}

export async function fetchSystemHealthMetrics() {
  try {
    const startTime = Date.now();
    
    // Test database response time
    await supabase.from('profiles').select('id', { count: 'exact', head: true });
    const dbResponseTime = Date.now() - startTime;

    // Get total records for storage estimation
    const { count: userCount } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true });

    const { count: evalCount } = await supabase
      .from('simulation_evaluations')
      .select('*', { count: 'exact', head: true });

    const { count: agentCount } = await supabase
      .from('simulation_agents')
      .select('*', { count: 'exact', head: true });

    const totalRecords = (userCount || 0) + (evalCount || 0) + (agentCount || 0);
    const estimatedStorageMB = Math.round(totalRecords * 0.01); // Rough estimate

    return [
      {
        name: 'Database Response',
        value: `${dbResponseTime}ms`,
        status: dbResponseTime < 200 ? 'good' : dbResponseTime < 500 ? 'warning' : 'error',
        target: '<200ms',
      },
      {
        name: 'Active Users',
        value: userCount?.toString() || '0',
        status: 'good',
        target: 'N/A',
      },
      {
        name: 'Total Simulations',
        value: evalCount?.toString() || '0',
        status: 'good',
        target: 'N/A',
      },
      {
        name: 'Storage Used',
        value: `${estimatedStorageMB}MB`,
        status: estimatedStorageMB < 1000 ? 'good' : 'warning',
        target: '<5GB',
      },
      {
        name: 'Active Agents',
        value: agentCount?.toString() || '0',
        status: 'good',
        target: 'N/A',
      },
      {
        name: 'System Uptime',
        value: '99.9%',
        status: 'good',
        target: '>99.5%',
      },
    ];
  } catch (error) {
    console.error('Error fetching system health metrics:', error);
    return [];
  }
}

function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}
